/**
 * Shared helper for polling the async floorplan parse job endpoint.
 *
 * Usage:
 *   const { jobId } = await (await fetch("/api/parse-floorplan", { method:"POST", body: fd, credentials:"include" })).json();
 *   const result = await pollFloorplanJob(jobId);
 */

export interface FloorplanParsed {
  lenderName: string;
  lenderKey: string;
  statementPeriod: { start?: string; end?: string };
  totals: {
    totalInterestPaid: number;
    totalFeesPaid: number;
    averageBalance: number;
    unitsSold: number;
    periodDays: number;
    avgDaysInStock: number;
  };
  feeBreakdown: {
    feeAdmin: number;
    feeAudit: number;
    feeCurtailment: number;
    feeDoc: number;
    feeTitle: number;
    feeOther: number;
  };
  receivables: Record<string, any>[];
  detectedFieldsConfidence: Record<string, number>;
  warnings: string[];
  accuracyCheck?: Record<string, any> | null;
  rawText?: string;
}

export interface FloorplanJobResult {
  statement: Record<string, any>;
  parsed: FloorplanParsed;
  lenderTerms: Record<string, any> | null;
  extractionMethod: string;
  difficulty: string | null;
  success: boolean;
  termsApplied: boolean;
}

/** Maximum time to wait for a floorplan job to complete (8 minutes). */
const POLL_TIMEOUT_MS = 8 * 60 * 1_000;

/**
 * Maximum number of consecutive network-level errors (fetch throws or 5xx
 * responses) before the poller surfaces an error to the caller.  Each retry
 * uses a longer interval (6 s) so a server that is restarting gets time to
 * come back up before we give up.
 */
const MAX_POLL_NETWORK_RETRIES = 5;

/**
 * Polls /api/parse-floorplan/status/:jobId using chained setTimeout
 * (avoids overlapping requests that setInterval could produce when the
 * server is slow).  Resolves with the extraction result when done,
 * rejects with an Error on failure, connection loss, or after POLL_TIMEOUT_MS.
 *
 * 5xx HTTP responses and network-level fetch errors are retried up to
 * MAX_POLL_NETWORK_RETRIES times with a 6 s interval before rejecting, so
 * a brief server restart does not surface a spurious error to the dealer.
 *
 * @param jobId      - UUID returned by POST /api/parse-floorplan
 * @param onResult   - optional callback invoked with the result (before resolve)
 * @param intervalMs - poll interval in milliseconds (default 2000)
 */
export function pollFloorplanJob(
  jobId: string,
  onResult?: (result: FloorplanJobResult) => void,
  intervalMs = 2000,
): Promise<FloorplanJobResult> {
  return new Promise<FloorplanJobResult>((resolve, reject) => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let networkRetryCount = 0;

    const deadline = setTimeout(() => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      reject(new Error("Extraction timed out. Please try uploading again."));
    }, POLL_TIMEOUT_MS);

    function cleanup() {
      clearTimeout(deadline);
    }

    async function poll() {
      if (cancelled) return;
      try {
        const statusRes = await fetch(`/api/parse-floorplan/status/${jobId}`, {
          credentials: "include",
        });
        if (cancelled) return;

        if (!statusRes.ok) {
          // 5xx / 502 / 503 during a server restart — retry silently a few times
          // before surfacing an error. The server resets in-flight floorplan jobs
          // to 'pending' on startup so the job itself survives the restart.
          if (statusRes.status >= 500 && networkRetryCount < MAX_POLL_NETWORK_RETRIES) {
            networkRetryCount++;
            timer = setTimeout(poll, 6000);
            return;
          }
          networkRetryCount = 0;
          let message = `Processing job failed (HTTP ${statusRes.status}).`;
          try {
            const errJson = await statusRes.json();
            if (errJson?.error) message = errJson.error;
            else if (errJson?.message) message = errJson.message;
          } catch {}
          cleanup();
          reject(new Error(message));
          return;
        }

        // Successful response — reset network-error counter.
        networkRetryCount = 0;
        const statusJson = await statusRes.json();
        if (cancelled) return;

        if (statusJson.status === "processing" || statusJson.status === "queued" || statusJson.status === "pending") {
          timer = setTimeout(poll, intervalMs);
          return;
        }

        if (statusJson.status === "error") {
          cleanup();
          reject(new Error(statusJson.error || "Processing failed."));
          return;
        }

        const result = statusJson.result as FloorplanJobResult;
        cleanup();
        if (onResult) onResult(result);
        resolve(result);
      } catch (err: any) {
        if (cancelled) return;
        // Network/fetch error — may indicate the server is briefly restarting.
        // Retry a few times with a longer interval before surfacing an error.
        if (networkRetryCount < MAX_POLL_NETWORK_RETRIES) {
          networkRetryCount++;
          timer = setTimeout(poll, 6000);
          return;
        }
        networkRetryCount = 0;
        cleanup();
        reject(new Error("Could not reach the server. Please try again."));
      }
    }

    timer = setTimeout(poll, 1500);
  });
}
